import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Deal {
  id: string;
  name: string;
  category: string;
  original_price: number;
  deal_price: number;
  image_url: string;
  badge: string;
  badge_color: string;
  tag: string;
  link: string;
  is_active: boolean;
  sort_order: number;
}

const BADGE_COLORS = [
  { label: 'Red-Orange', value: 'from-red-500 to-orange-500' },
  { label: 'Violet-Indigo', value: 'from-violet-500 to-indigo-500' },
  { label: 'Amber-Yellow', value: 'from-amber-500 to-yellow-500' },
  { label: 'Emerald-Teal', value: 'from-emerald-500 to-teal-500' },
  { label: 'Pink-Rose', value: 'from-pink-500 to-rose-500' },
  { label: 'Cyan-Blue', value: 'from-cyan-500 to-blue-500' },
];

const CATEGORY_OPTIONS = ['T-Shirts', 'Jeans', 'Hoodies', 'Shorts', 'Track Pants'];

const TAG_OPTIONS = ['Best Seller', 'Flash Deal', 'Limited Stock', 'Today Only', 'Hot Pick', 'New Arrival'];

const EMPTY_DEAL: Omit<Deal, 'id'> = {
  name: '',
  category: 'T-Shirts',
  original_price: 0,
  deal_price: 0,
  image_url: '',
  badge: '40% OFF',
  badge_color: 'from-red-500 to-orange-500',
  tag: 'Flash Deal',
  link: '/category',
  is_active: true,
  sort_order: 0,
};

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export default function TodaysDealsPanel({ onToast }: Props) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Omit<Deal, 'id'>>(EMPTY_DEAL);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<Omit<Deal, 'id'>>(EMPTY_DEAL);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('today_deals')
      .select('*')
      .order('sort_order', { ascending: true });
    if (!error && data) setDeals(data as Deal[]);
    setLoading(false);
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleToggleActive = async (deal: Deal) => {
    const { error } = await supabase
      .from('today_deals')
      .update({ is_active: !deal.is_active, updated_at: new Date().toISOString() })
      .eq('id', deal.id);
    if (!error) {
      setDeals(prev => prev.map(d => d.id === deal.id ? { ...d, is_active: !d.is_active } : d));
      onToast(deal.is_active ? 'Deal hide kar diya!' : 'Deal active kar diya!', 'success');
    } else {
      onToast('Kuch galat ho gaya', 'error');
    }
  };

  const handleStartEdit = (deal: Deal) => {
    setEditingId(deal.id);
    setEditForm({
      name: deal.name,
      category: deal.category,
      original_price: deal.original_price,
      deal_price: deal.deal_price,
      image_url: deal.image_url,
      badge: deal.badge,
      badge_color: deal.badge_color,
      tag: deal.tag,
      link: deal.link,
      is_active: deal.is_active,
      sort_order: deal.sort_order,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    if (!editForm.name.trim() || !editForm.image_url.trim()) {
      onToast('Name aur Image URL zaroori hai!', 'error');
      return;
    }
    if (editForm.deal_price >= editForm.original_price) {
      onToast('Deal price, original price se kam honi chahiye!', 'error');
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('today_deals')
      .update({ ...editForm, updated_at: new Date().toISOString() })
      .eq('id', editingId);
    setSaving(false);
    if (!error) {
      setDeals(prev => prev.map(d => d.id === editingId ? { ...d, ...editForm } : d));
      setEditingId(null);
      onToast('Deal update ho gaya!', 'success');
    } else {
      onToast('Save nahi hua. Try again.', 'error');
    }
  };

  const handleAddDeal = async () => {
    if (!addForm.name.trim() || !addForm.image_url.trim()) {
      onToast('Name aur Image URL zaroori hai!', 'error');
      return;
    }
    if (addForm.deal_price >= addForm.original_price) {
      onToast('Deal price, original price se kam honi chahiye!', 'error');
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from('today_deals')
      .insert([{ ...addForm, sort_order: deals.length + 1 }])
      .select()
      .single();
    setSaving(false);
    if (!error && data) {
      setDeals(prev => [...prev, data as Deal]);
      setAddForm(EMPTY_DEAL);
      setShowAddForm(false);
      onToast('Naya deal add ho gaya!', 'success');
    } else {
      onToast('Deal add nahi hua. Try again.', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    const { error } = await supabase.from('today_deals').delete().eq('id', id);
    setDeletingId(null);
    if (!error) {
      setDeals(prev => prev.filter(d => d.id !== id));
      onToast('Deal delete ho gaya!', 'success');
    } else {
      onToast('Delete nahi hua. Try again.', 'error');
    }
  };

  const discountPct = (orig: number, deal: number) =>
    orig > 0 ? Math.round((1 - deal / orig) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
              <i className="ri-fire-fill text-white text-lg"></i>
            </div>
            <div>
              <p className="text-gray-900 font-bold text-base">Today&apos;s Deals Manager</p>
              <p className="text-gray-400 text-xs">Home page ke flash deals yahan se control karo</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchDeals}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>Refresh
            </button>
            <button
              onClick={() => { setShowAddForm(true); setAddForm(EMPTY_DEAL); }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>Naya Deal Add Karo
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <div className="bg-orange-50 rounded-xl p-3 border border-orange-100 text-center">
            <p className="text-orange-600 font-black text-xl">{deals.length}</p>
            <p className="text-orange-500/70 text-[10px] font-semibold">Total Deals</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 border border-green-100 text-center">
            <p className="text-green-600 font-black text-xl">{deals.filter(d => d.is_active).length}</p>
            <p className="text-green-500/70 text-[10px] font-semibold">Active</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 text-center">
            <p className="text-gray-600 font-black text-xl">{deals.filter(d => !d.is_active).length}</p>
            <p className="text-gray-500/70 text-[10px] font-semibold">Hidden</p>
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>
        </div>
        <div>
          <p className="text-amber-800 font-bold text-sm">Kaise Kaam Karta Hai?</p>
          <ul className="text-amber-700 text-xs mt-1 space-y-1">
            <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Yahan se deals add/edit/delete karo — home page pe turant update ho jaayega</li>
            <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Toggle se deal ko show/hide karo bina delete kiye</li>
            <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Sirf active deals home page pe dikhenge</li>
          </ul>
        </div>
      </div>

      {/* Add Deal Form */}
      {showAddForm && (
        <DealForm
          title="Naya Deal Add Karo"
          form={addForm}
          onChange={setAddForm}
          onSave={handleAddDeal}
          onCancel={() => setShowAddForm(false)}
          saving={saving}
          isNew
        />
      )}

      {/* Deals List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-orange-500 animate-spin"></div>
          <p className="text-gray-400 text-sm">Deals load ho rahe hain...</p>
        </div>
      ) : deals.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center">
            <i className="ri-price-tag-3-line text-3xl text-orange-300"></i>
          </div>
          <div className="text-center">
            <p className="text-gray-700 font-semibold">Koi deal nahi hai</p>
            <p className="text-gray-400 text-sm mt-1">Upar &quot;Naya Deal Add Karo&quot; button se pehla deal add karo</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {deals.map((deal) => (
            <div key={deal.id}>
              {editingId === deal.id ? (
                <DealForm
                  title={`Edit: ${deal.name}`}
                  form={editForm}
                  onChange={setEditForm}
                  onSave={handleSaveEdit}
                  onCancel={() => setEditingId(null)}
                  saving={saving}
                />
              ) : (
                <DealCard
                  deal={deal}
                  discountPct={discountPct(deal.original_price, deal.deal_price)}
                  onToggle={() => handleToggleActive(deal)}
                  onEdit={() => handleStartEdit(deal)}
                  onDelete={() => handleDelete(deal.id)}
                  deleting={deletingId === deal.id}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Deal Card ──────────────────────────────────────────────────────────────
interface DealCardProps {
  deal: Deal;
  discountPct: number;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}

function DealCard({ deal, discountPct, onToggle, onEdit, onDelete, deleting }: DealCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden transition-all ${deal.is_active ? 'border-gray-100' : 'border-gray-200 opacity-60'}`}>
      <div className="flex gap-0">
        {/* Image */}
        <div className="relative shrink-0 w-24 h-28 overflow-hidden">
          <img src={deal.image_url} alt={deal.name} className="w-full h-full object-cover object-top" />
          {!deal.is_active && (
            <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
              <span className="text-white text-[9px] font-black bg-gray-800/80 px-2 py-1 rounded-full">HIDDEN</span>
            </div>
          )}
          <div className="absolute top-1.5 left-1.5">
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-black text-white bg-gradient-to-r ${deal.badge_color}`}>
              {deal.badge}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0">
              <p className="text-[9px] font-black uppercase tracking-widest text-gray-400">{deal.category}</p>
              <p className="text-gray-900 font-bold text-sm leading-tight line-clamp-1">{deal.name}</p>
            </div>
            {/* Active toggle */}
            <button
              onClick={onToggle}
              className={`relative w-10 h-5 rounded-full transition-all cursor-pointer shrink-0 mt-0.5 ${deal.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow ${deal.is_active ? 'translate-x-5' : 'translate-x-0'}`}></span>
            </button>
          </div>

          {/* Prices */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-gray-900 font-black text-base">₹{deal.deal_price}</span>
            <span className="text-gray-300 line-through text-xs">₹{deal.original_price}</span>
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black text-white bg-gradient-to-r from-red-500 to-orange-500">
              {discountPct}% OFF
            </span>
          </div>

          {/* Tag + Link */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-gray-100 text-gray-600">{deal.tag}</span>
            <span className="text-[9px] text-gray-400 truncate max-w-[100px]">{deal.link}</span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={onEdit}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-2-line text-xs"></i>Edit
            </button>
            {confirmDelete ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-red-600 font-semibold">Sure?</span>
                <button
                  onClick={onDelete}
                  disabled={deleting}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 text-white text-[11px] font-bold hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {deleting ? <i className="ri-loader-4-line animate-spin text-xs"></i> : <i className="ri-delete-bin-line text-xs"></i>}
                  Delete
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-2 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-[11px] font-bold hover:bg-gray-200 transition-all cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-500 text-[11px] font-bold hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line text-xs"></i>Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Deal Form ──────────────────────────────────────────────────────────────
interface DealFormProps {
  title: string;
  form: Omit<Deal, 'id'>;
  onChange: (form: Omit<Deal, 'id'>) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isNew?: boolean;
}

function DealForm({ title, form, onChange, onSave, onCancel, saving, isNew }: DealFormProps) {
  const set = (key: keyof Omit<Deal, 'id'>, value: string | number | boolean) =>
    onChange({ ...form, [key]: value });

  const discountPct = form.original_price > 0
    ? Math.round((1 - form.deal_price / form.original_price) * 100)
    : 0;

  // Auto-update badge when prices change
  const handlePriceChange = (key: 'original_price' | 'deal_price', val: number) => {
    const updated = { ...form, [key]: val };
    const pct = updated.original_price > 0
      ? Math.round((1 - updated.deal_price / updated.original_price) * 100)
      : 0;
    onChange({ ...updated, badge: `${pct}% OFF` });
  };

  // Auto-update link when category changes
  const handleCategoryChange = (cat: string) => {
    onChange({ ...form, category: cat, link: `/category?cat=${cat}` });
  };

  return (
    <div className="bg-white rounded-2xl border border-orange-200 overflow-hidden">
      {/* Form header */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-orange-50 to-red-50 border-b border-orange-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center">
            <i className={`${isNew ? 'ri-add-line' : 'ri-edit-2-line'} text-white text-sm`}></i>
          </div>
          <p className="text-gray-900 font-bold text-sm">{title}</p>
        </div>
        <button onClick={onCancel} className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-all cursor-pointer">
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Deal Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            placeholder="e.g. Baggy Street Track Pants"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Category *</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_OPTIONS.map(cat => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                  form.category === cat ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Prices */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Original Price (₹) *</label>
            <input
              type="number"
              value={form.original_price || ''}
              onChange={e => handlePriceChange('original_price', Number(e.target.value))}
              placeholder="999"
              min={0}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Deal Price (₹) *</label>
            <input
              type="number"
              value={form.deal_price || ''}
              onChange={e => handlePriceChange('deal_price', Number(e.target.value))}
              placeholder="599"
              min={0}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
            />
          </div>
        </div>

        {/* Discount preview */}
        {discountPct > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50 border border-green-100">
            <i className="ri-price-tag-3-fill text-green-500 text-sm"></i>
            <span className="text-green-700 text-xs font-bold">
              {discountPct}% discount — Customer ₹{form.original_price - form.deal_price} bachayega!
            </span>
          </div>
        )}

        {/* Image URL */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Product Image URL *</label>
          <input
            type="url"
            value={form.image_url}
            onChange={e => set('image_url', e.target.value)}
            placeholder="https://..."
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
          />
          {form.image_url && (
            <div className="mt-2 w-20 h-24 rounded-xl overflow-hidden border border-gray-200">
              <img src={form.image_url} alt="preview" className="w-full h-full object-cover object-top" />
            </div>
          )}
        </div>

        {/* Badge + Tag */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Badge Text</label>
            <input
              type="text"
              value={form.badge}
              onChange={e => set('badge', e.target.value)}
              placeholder="40% OFF"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Tag</label>
            <select
              value={form.tag}
              onChange={e => set('tag', e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none bg-white"
            >
              {TAG_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Badge Color */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Badge Color</label>
          <div className="flex flex-wrap gap-2">
            {BADGE_COLORS.map(bc => (
              <button
                key={bc.value}
                onClick={() => set('badge_color', bc.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap border-2 ${
                  form.badge_color === bc.value ? 'border-gray-900' : 'border-transparent'
                }`}
              >
                <span className={`w-4 h-4 rounded-full bg-gradient-to-r ${bc.value} shrink-0`}></span>
                {bc.label}
              </button>
            ))}
          </div>
        </div>

        {/* Link */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">Link (auto-set from category)</label>
          <input
            type="text"
            value={form.link}
            onChange={e => set('link', e.target.value)}
            placeholder="/category?cat=T-Shirts"
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
          />
        </div>

        {/* Active toggle */}
        <div className={`rounded-xl border p-3 transition-all ${form.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-900 font-bold text-sm">{form.is_active ? 'Deal Active' : 'Deal Hidden'}</p>
              <p className={`text-xs ${form.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                {form.is_active ? 'Home page pe dikhega' : 'Home page pe nahi dikhega'}
              </p>
            </div>
            <button
              onClick={() => set('is_active', !form.is_active)}
              className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${form.is_active ? 'translate-x-6' : 'translate-x-0'}`}></span>
            </button>
          </div>
        </div>

        {/* Save / Cancel */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={onSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? (
              <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
            ) : (
              <><i className="ri-save-line"></i>{isNew ? 'Deal Add Karo' : 'Changes Save Karo'}</>
            )}
          </button>
          <button
            onClick={onCancel}
            className="px-5 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
